/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useEffect, useState } from 'react'
import ActionsMenu from '~/components/shared/ActionsMenu'
import { useModal } from '~/contexts/ModalContext'
import ArtifactProperties from './ArtifactProperties'

interface PackageEntry {
  id: number
  docTypeId: number | string
  selected?: boolean
  name?: string
  metadata?: Array<{ metadataTemplateId: number; value: any }>
}

interface TableMenuProps {
  item: {
    id: number
    level: string
    entries: PackageEntry[]
  }
  setPckgData: (updater: (prev: any) => any) => void
  removeEntry: () => void
  duplicateEntry: () => void
  index: number
}

interface MenuItem {
  value: string
  action: () => void
  disabled?: boolean
}

const TableMenu: React.FC<TableMenuProps> = ({ item, setPckgData, removeEntry, duplicateEntry, index }) => {
  const { showModal } = useModal()
  const [isOpen, setIsOpen] = useState(false)
  const [items, setItems] = useState<MenuItem[]>([
    {
      value: 'LOADING...',
      action: () => {},
      disabled: true
    }
  ])

  const setOptions = () => {
    const menu: MenuItem[] = []
    menu.push({
      value: 'ACTION_DUPLICATE',
      action: () => {
        duplicateEntry()
      }
    })
    menu.push({
      value: 'ACTION_REMOVE',
      action: () => {
        removeEntry()
      }
    })
    menu.push({
      value: 'Properties',
      action: () => {
        const selectedEntries = getSelectedRows(item?.entries)
        // Hydrate the modal from the entry that already has saved metadata (if any).
        // For multi-select: prefer the clicked entry's metadata, otherwise fall back
        // to the first selected entry that has metadata persisted.
        const savedMetadata = item?.entries?.[index]?.metadata ?? selectedEntries.find((e) => e?.metadata)?.metadata
        if (
          selectedEntries?.length > 1 &&
          getSelectedRows(item?.entries)
            .map((e) => e.id)
            .includes(item?.entries?.[index]?.id)
        ) {
          const selectedIndexes = item?.entries?.reduce<number[]>((acc, e, idx) => {
            if (e?.selected) acc.push(idx)
            return acc
          }, [])
          // Bulk edit: pass every selected entry's local metadata so the modal
          // can compute a per-property "mixed" flag across the selection.
          const savedMetadataList = selectedEntries.map((e) => e?.metadata)
          showModal(
            <ArtifactProperties
              level={item?.level}
              setPckgData={setPckgData}
              documentTypeId={item?.entries?.[index]?.docTypeId}
              docTypeIds={selectedEntries.map((e) => Number(e.docTypeId))}
              entryIndex={index}
              options={{ selectedIds: getSelectedRows(item?.entries).map((e) => e.id), selectedIndexes }}
              artifact={{ id: item?.entries?.[index]?.id, name: item?.entries?.[index]?.name || '' }}
              savedMetadata={savedMetadata}
              savedMetadataList={savedMetadataList}
            />,
            { keepParentModalsOpen: true }
          )
        } else {
          showModal(
            <ArtifactProperties
              level={item?.level}
              setPckgData={setPckgData}
              documentTypeId={item?.entries?.[index]?.docTypeId}
              docTypeIds={selectedEntries.map((e) => Number(e.docTypeId))}
              entryIndex={index}
              artifact={{ id: item?.entries?.[index]?.id, name: item?.entries?.[index]?.name || '' }}
              savedMetadata={savedMetadata}
            />,
            { keepParentModalsOpen: true }
          )
        }
      }
    })
    setItems(menu)
  }

  const getSelectedRows = (items: PackageEntry[]): PackageEntry[] => {
    return items.filter(function (item) {
      return item?.selected === true
    })
  }

  useEffect(() => {
    setOptions()
  }, [item, isOpen])

  return (
    <>
      <ActionsMenu
        menuId={`action-menu-workflows-${item.id}`}
        onToggle={(open) => {
          setIsOpen(open)
        }}
        items={items}
      />
    </>
  )
}

export default TableMenu
